import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Time24Input } from '../../components/ui/time24-input';
import { useTranslation, useI18nStore } from '../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);


const box = 'w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm';
const F = ({ label, children }) => <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</span>{children}</label>;

function AnswerInput({ f, value, onChange }) {
  const id = `ss-admin-ans-${f.key}`;
  if (f.type === 'boolean') {
    return <select value={value === true ? 'true' : value === false ? 'false' : ''} onChange={(e) => onChange(e.target.value === '' ? '' : e.target.value === 'true')} className={box} data-testid={id}><option value="">—</option><option value="true">{tt('yes')}</option><option value="false">{tt('no')}</option></select>;
  }
  if (f.type === 'select') {
    return <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={box} data-testid={id}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  }
  return <Input type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={id} />;
}

export function AdminEditDialog({ r, phase, onClose, onSaved }) {
  useTranslation();
  const cur = r[phase] || {};
  const completing = phase === 'stop' && !r.stop;
  const fields = (r.custom_fields || []).filter((f) => f.ss_phase === phase && f.type !== 'photo');
  const [time, setTime] = useState(cur.entered_time || '');
  const [km, setKm] = useState(cur.km ?? '');
  const [loc, setLoc] = useState(cur.lat != null ? `${cur.lat}, ${cur.lng}` : '');
  const [ans, setAns] = useState(() => ({ ...(cur.answers || {}) }));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const m = loc.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,; ]\s*(-?\d+(?:\.\d+)?)$/);
    if (loc.trim() && (!m || Math.abs(+m[1]) > 90 || Math.abs(+m[2]) > 180)) return toast.error(tt('locInvalid'));
    if (completing && !time) return toast.error(tt('endTimeRequired'));
    if (reason.trim().length < 3) return toast.error(tt('reasonMin'));
    setBusy(true);
    try {
      await api.post(`/planning/assignments/${r.assignment_id}/start-stop/admin-edit`, {
        phase, entered_time: time || null, km: km === '' ? null : Number(km),
        answers: Object.fromEntries(fields.map((f) => [f.key, ans[f.key]]).filter(([, v]) => v !== undefined && v !== '')),
        location: m ? { lat: +m[1], lng: +m[2] } : null, reason: reason.trim(),
      });
      toast.success(completing ? tt('stopCompleted') : tt('recordCorrected'));
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(', ') : d || tt('saveFailed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="ss-admin-edit-dialog">
        <DialogHeader>
          <DialogTitle>{completing ? tt('completeStop') : tt(phase === 'start' ? 'editTitleStart' : 'editTitleStop')}</DialogTitle>
          <DialogDescription>{r.employee_name} · {r.route_code || '—'} · {r.date}. {tt('editDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label={`${tt(phase === 'start' ? 'startTime' : 'endTime')}${completing ? ' *' : ''}`}><Time24Input value={time} onChange={setTime} className={box} testId="ss-admin-time" /></F>
            <F label={tt(phase === 'start' ? 'startKm' : 'endKm')}><Input type="number" min={0} value={km} onChange={(e) => setKm(e.target.value)} className="h-10" data-testid="ss-admin-km" placeholder={phase === 'stop' && r.start?.km != null ? `≥ ${r.start.km}` : ''} /></F>
          </div>
          {fields.map((f) => <F key={f.key} label={f.label}><AnswerInput f={f} value={ans[f.key]} onChange={(v) => setAns((p) => ({ ...p, [f.key]: v }))} /></F>)}
          <F label={tt('locLabel')}><Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="50.9959, 5.2033" className="h-10" data-testid="ss-admin-loc" /></F>
          <F label={tt('reasonLabel')}><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={`${box} h-auto py-2`} placeholder={tt('reasonPh')} data-testid="ss-admin-reason" /></F>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="h-10 px-3 rounded-lg text-sm">{tt('cancel')}</button>
            <button type="button" onClick={save} disabled={busy} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="ss-admin-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{completing ? tt('complete') : tt('save')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
